import React, {useCallback, useEffect, useMemo, useState} from "react";
import {inject, observer} from "mobx-react";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
// local
import PresentationCardWrapper from "../../PresentationCardWrapper";
import ContactsEdit from "./ContactForms/ContactsEdit";
import ContactCard from "components/ContactCard";
// import MiniSelect from "components/selects/MiniSelect";
import MultipleSelect from "components/selects/MultipleSelect";
import EmptyData from "components/EmptyData";
import {LockValue} from "components/LockValue";
import {ModalContactMember} from "components/modals/ModalContactMember";
import {IconUsers} from "components/icons";
import {Info} from "components/Info";
// import {color} from "constants/color.consts";
// import {useSavedFilters} from "helpers/userHooks";
import {getFullName, hasPermission} from "helpers/helper";

const ContactPersons = inject("store")(
  observer(({store: {organization, auth, clientStore}, edit, setEdit}) => {
    const {t} = useTranslation();
    // const location = useLocation();
    const {organizationId} = useParams();

    const {organizationDetail, organizationRolesMembers, organizationMembersSelectList} =
      organization;
    const {user, activeOrganization, memberId} = auth;
    const {language} = clientStore;

    // const [filters, setRegionFilter] = useSavedFilters({
    //   key: "organizationContactAvailableInFilter",
    //   initialValue: {availableIn: location?.state?.availableIn},
    //   storage: "session",
    // });

    const [initialContacts, setInitialContacts] = useState(null);
    const initialContactsIds = useMemo(
      () => initialContacts?.map((contact) => contact.UserId),
      [initialContacts]
    );
    const [contactMembers, setContactMembers] = useState(null);

    const addedContactMembers = useMemo(
      () =>
        contactMembers
          ? contactMembers
              ?.filter((member) => !initialContactsIds?.includes(member.id))
              .sort((a, b) =>
                (a.lastName ?? "").localeCompare(b.lastName ?? "", language, {
                  sensitivity: "base",
                })
              )
              .map((member) => ({
                value: member.id,
                label: getFullName(member.firstName, member.lastName),
              }))
          : [],
      [contactMembers, initialContactsIds, language]
    );
    const ourOrganizationMembers = useMemo(
      () => Object.values(organizationRolesMembers).flat(),
      [organizationRolesMembers]
    );

    const [isModalContactMemberId, setModalContactMemberId] = useState(null);

    const isOwnOrganization = useMemo(
      () => organizationId === activeOrganization.id && !!organizationDetail?.isActive,
      [organizationId, activeOrganization, organizationDetail?.isActive]
    );

    const modalContactUserInfo = useMemo(
      () =>
        isOwnOrganization
          ? ourOrganizationMembers?.find(
              (member) => member.UserId === isModalContactMemberId
            )?.User
          : contactMembers?.find((member) => member.id === isModalContactMemberId),
      [contactMembers, ourOrganizationMembers, isModalContactMemberId, isOwnOrganization]
    );

    // const regionSelectOptions = useMemo(
    //   () =>
    //     [{label: "SELECT_ALL_PROJECT_REGIONS", value: null}].concat(
    //       [
    //         ...new Set(
    //           initialContacts
    //             ?.filter((contact) => !!contact.rftsProjectAvailableIn)
    //             .map((contact) => contact.rftsProjectAvailableIn)
    //             .flat() ?? []
    //         ),
    //       ].map((item) => ({label: t(item), value: item}))
    //     ),
    //   [initialContacts, t]
    // );

    const defaultRegion = undefined;
    // const defaultRegion = useMemo(
    //   () =>
    //     regionSelectOptions.find((region) => region.value === filters?.availableIn)
    //       ?.value ?? null,
    //   [regionSelectOptions, filters]
    // );

    // const isRegionDropdown = useMemo(
    //   () => !isOwnOrganization && regionSelectOptions?.length > 1,
    //   [isOwnOrganization, regionSelectOptions]
    // );

    // const customTitleSelect = () =>
    //   isRegionDropdown && (
    //     <MiniSelect
    //       list={regionSelectOptions}
    //       defaultValue={defaultRegion}
    //       addClass="ml-4"
    //       addDropdownClass="organization-page__contact-persons-dropdown"
    //       itemAsObject
    //       fullWidthDropdownLabel
    //       itemColor={color.green}
    //       onChange={handleSortRegions}
    //       tinyLabel
    //       boldLabel
    //     />
    //   );

    const suffixTitleContent = () =>
      !isOwnOrganization &&
      !!addedContactMembers && (
        <LockValue canPermissions={["READ_OTHER_CONTACT_PERSONS"]} noHideOurInfo justHide>
          <div className="contact-members-select__wrapper">
            <MultipleSelect
              title="SELECT_ANOTHER_ORANIZATION_MEMBERS_BUTTON"
              list={addedContactMembers}
              noMultipleChange
              notShowingSelections
              noNeedSortingList
              tiny
              uppercaseButton
              boldLabel
              titleInButton
              value={null}
              disabled={!addedContactMembers.length}
              onChange={addHandledContact}
              addClass="mr-0 contact-members-select"
            />
            <Info
              centerText
              tooltip={t("SELECT_ANOTHER_ORANIZATION_MEMBERS_TOOLTIP").format(
                organizationDetail.name
              )}
            />
          </div>
        </LockValue>
      );

    const innerSort = useCallback(
      (list) =>
        list.sort(
          (a, b) =>
            b.rftsProjectAvailableIn?.length - a.rftsProjectAvailableIn?.length ||
            (a.User?.lastName ?? "").localeCompare(b.User?.lastName ?? "", language, {
              sensitivity: "base",
            })
        ),
      [language]
    );
    const contactsSort = useCallback(
      (list) => {
        const memberIds = (item) => item.OurOrganizationMemberIds;
        const myContact = (item) => item.OurOrganizationMemberIds?.includes(memberId);
        const ourContact = (item) => !!memberIds(item) && !myContact(item);
        const myContacts = innerSort(list.filter(myContact));
        const ourContacts = innerSort(list.filter(ourContact));
        const nobodyContacts = innerSort(
          list.filter((item) => !item.HandledOrganizationMemberId)
        );

        return [...myContacts, ...ourContacts, ...nobodyContacts];
      },
      [memberId, innerSort]
    );

    const contacts = useMemo(
      () =>
        contactsSort(
          defaultRegion
            ? initialContacts?.filter((item) =>
                item.rftsProjectAvailableIn?.includes(defaultRegion)
              )
            : initialContacts ?? []
        ),
      [initialContacts, defaultRegion, contactsSort]
    );

    const closeEdit = (reload = false) => {
      setEdit({...edit, contacts: false});
      if (reload) {
        organization.getOrganizationMembers("SHOW_OUR_PROFILE_CONTACT_CARDS_USER");
      }
    };

    const getContactInfo = () => {
      const contact = contacts.find(
        (contact) => contact.UserId === isModalContactMemberId
      );
      return (
        !!contact?.HandledOrganizationMemberId
          ? organization.getAnotherOrganizationHandledContact
          : organization.getAnotherOrganizationContact
      )(organizationId, isModalContactMemberId).then((data) => ({
        ...data,
        OurOrganizationUsers: contact?.OurOrganizationMemberIds?.map((id) => {
          const {User, UserId} = ourOrganizationMembers.find(
            (member) => member.id === id
          );
          return {id: UserId, ...User};
        }),
      }));
    };

    const getContactsInfo = useCallback(
      async () =>
        (hasPermission(["READ_OTHER_CONTACT_PERSONS"]) || isOwnOrganization) &&
        organization.getAnotherOrganizationContacts(organizationId).then(async (data) => {
          const regions = isOwnOrganization
            ? []
            : await organization.getAnotherOrganizationContactsRegions(organizationId);
          const otherMembers = isOwnOrganization
            ? null
            : await organization.getAnotherOrganizationMembers(
                organizationId,
                "SHOW_OUR_PROFILE_CONTACT_CARDS_USER"
              );
          setContactMembers(otherMembers);
          const uniqueContacts = [
            ...[...data.handled, ...data.default]
              .reduce(
                (acc, item) =>
                  acc.has(item.UserId)
                    ? acc.set(item.UserId, {...acc.get(item.UserId), ...item})
                    : acc.set(item.UserId, item),
                new Map()
              )
              .values(),
          ];
          const enrichedContacts = uniqueContacts.map((contact) => ({
            ...contact,
            ...(isOwnOrganization
              ? ourOrganizationMembers.find((member) => member.UserId === contact.UserId)
              : {User: otherMembers?.find((member) => member.id === contact.UserId)}),
            ...(regions.find((item) => item.UserId === contact.UserId) ?? {}),
            OurOrganizationUsers: contact?.OurOrganizationMemberIds?.map((id) => {
              const {User, UserId} = ourOrganizationMembers.find(
                (member) => member.id === id
              );
              return {id: UserId, ...User};
            }),
          }));
          setInitialContacts(enrichedContacts);
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [organizationId, organization, isOwnOrganization, ourOrganizationMembers]
    );

    // const handleSortRegions = (availableIn) =>
    //   setRegionFilter(availableIn ? {availableIn} : {});

    const addHandledContact = async (value) => {
      const UserId = Array.isArray(value) ? value[0] : value;
      await organization.createAnotherOrganizationHandledContact(organizationId, {
        UserId,
      });
      await getContactsInfo();
    };
    const deleteHandledContact = async (id) => {
      await organization.deleteAnotherOrganizationHandledContact(organizationId, id);
      await getContactsInfo();
    };

    const updateHandledContact = (id, values) =>
      organization.updateAnotherOrganizationHandledContact(organizationId, id, values);

    useEffect(() => {
      isOwnOrganization
        ? organization.getOrganizationMembers("SHOW_OUR_PROFILE_CONTACT_CARDS_USER")
        : organization.getOrganizationMembers();
      return () => organization.getOrganizationMembers();
    }, [organization, isOwnOrganization]);


    useEffect(() => {
      !!ourOrganizationMembers.length && getContactsInfo();
    }, [getContactsInfo, ourOrganizationMembers.length, organization]);

    return edit.contacts ? (
      <ContactsEdit
        members={organizationMembersSelectList}
        contacts={contacts}
        closeEdit={closeEdit}
      />
    ) : (
      <PresentationCardWrapper
        title={t("CONTACT_PERSONS")}
        icon={<IconUsers />}
        edit={edit}
        setEdit={setEdit}
        // customSelect={customTitleSelect()}
        // titleToolTip={isRegionDropdown && "SELECT_PROJECT_REGIONS_TOOLTIP"}
        suffixTitleContent={suffixTitleContent()}
        editName="contacts"
        editPermissions={["EDIT_OUR_PROFILE_MAIN_INFO"]}
        isActive={isOwnOrganization}
        addClass="__contact-person fg2"
      >
        <LockValue
          canPermissions={["READ_OTHER_CONTACT_PERSONS"]}
          noHideOurInfo
          fullSubcribeButton
        >
          <div className="contact-person_wrapper webkit-msedge-chrome-nicer-scrollbar">
            {!!contacts?.length ? (
              contacts?.map((contact) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  currentUser={user}
                  selectedRegion={defaultRegion}
                  isActive={isOwnOrganization}
                  onClick={setModalContactMemberId}
                />
              ))
            ) : (
              <EmptyData />
            )}
            {isModalContactMemberId && (
              <ModalContactMember
                contactId={isModalContactMemberId}
                contactUserInfo={modalContactUserInfo}
                getData={getContactInfo}
                organizationName={organizationDetail.name}
                isActive={isOwnOrganization}
                currentUser={user}
                members={ourOrganizationMembers}
                handleToggle={(id, status) =>
                  status ? addHandledContact(id) : deleteHandledContact(id)
                }
                handleRegions={updateHandledContact}
                onClose={() => setModalContactMemberId(null)}
              />
            )}
          </div>
        </LockValue>
      </PresentationCardWrapper>
    );
  })
);

export default ContactPersons;
